import React, { Component } from 'react';
import classNames from 'classnames';
import { PropTypes } from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import moment from 'moment';
import GiftIcon from 'react-material-icons/icons/action/card-giftcard';
import MagnifierIcon from 'react-material-icons/icons/action/search';
import {
  Table,
  TableBody,
  TableHeader,
  TableHeaderColumn,
  TableRow,
  TableRowColumn,
} from 'material-ui/Table';
import {
  ORDER_STATUS_DEALER_ACCEPTED,
  ORDER_STATUS_DEALER_CANCELLED,
  ORDER_STATUS_DEALER_DECLINED,
  ORDER_STATUS_CONSUMER_CANCELLED,
  ORDER_STATUS_DEALER_DELIVERED,
} from '@chama/utils/constants/status';
import { formatPrice } from '@chama/utils/functions/currency';
import Tooltip from '../Tooltip';
import Modal from '../Modal';
import getStatusLabel from '../../get-order-status-label';
import HistoryOrderDetails from '../HistoryOrderDetails';
import './index.css';

const OK_STATUSES = [ORDER_STATUS_DEALER_DELIVERED];

const WARN_STATUSES = [ORDER_STATUS_DEALER_ACCEPTED];

const DANGER_STATUSES = [
  ORDER_STATUS_DEALER_DECLINED,
  ORDER_STATUS_DEALER_CANCELLED,
  ORDER_STATUS_CONSUMER_CANCELLED,
];

const propTypes = {
  orders: PropTypes.array.isRequired,
  dealer: PropTypes.object.isRequired,
  isLoading: PropTypes.bool.isRequired,
  getOrderDetails: PropTypes.func.isRequired,
  periodId: PropTypes.string.isRequired,
  fetchMoreOrders: PropTypes.func.isRequired,
  batchSize: PropTypes.number.isRequired,
  ordersTotal: PropTypes.number.isRequired,
  dealerProfitAmount: PropTypes.number.isRequired,
  grossLeadFeeAmount: PropTypes.number.isRequired,
  grossMerchantValueAmount: PropTypes.number.isRequired,
  paidByVouchersAmount: PropTypes.number.isRequired,
  totalOrderedAmount: PropTypes.number.isRequired,
  paidOnDeliveryAmount: PropTypes.number.isRequired,
  analyticsTrack: PropTypes.func.isRequired,
};

class OrderTableLayout extends Component {
  constructor(props) {
    super(props);

    this.state = {
      hasScroll: false,
    };

    this.tableProps = {
      bodyStyle: {
        maxHeight: '700px',
      },
      selectable: false,
      ref: this.handleRef('tableBody'),
    };

    this.tableHeaderProps = {
      displaySelectAll: false,
      adjustForCheckbox: false,
    };

    this.tableBodyProps = {
      displayRowCheckbox: false,
    };

    this.detailsTooltip = 'Ver detalhes';

    this.dateFormatStr = 'DD/MM, HH:mm';
  }

  handleRef = refName => {
    return ref => {
      this[refName] = ref;
    };
  };

  onRowClick = clickedOrder => {
    const orderbyId = id => order => order.id === id;
    const { getOrderDetails, periodId } = this.props;

    if (clickedOrder) {
      const orderId = clickedOrder.id;

      getOrderDetails(periodId, clickedOrder.id).then(() => {
        const { details } = this.props.orders.find(orderbyId(orderId));

        this.setState({ orderDetails: details });
        this.openModal();
      });
    }
  };

  componentWillReceiveProps() {
    const { hasScroll } = this.state;

    if (!hasScroll) {
      const scrollableElem = this.getScrollableElement();
      const hasScrollbar = scrollableElem.offsetHeight < scrollableElem.scrollHeight;

      if (hasScrollbar) {
        this.setState({ hasScroll: true });
      }
    }
  }

  getScrollableElement = () => {
    return this.tableBody && this.tableBody.refs.tableBody.parentNode;
  };

  scrollTableToMax = () => {
    const scrollableElem = this.getScrollableElement();

    scrollableElem.scrollTop = scrollableElem.scrollHeight;
  };

  fetchMoreOrders = () => {
    const { periodId } = this.props;

    this.props.fetchMoreOrders(periodId).then(() => setTimeout(() => this.scrollTableToMax(), 50));
  };

  getNumberOfOrdersLeft = () => {
    const { ordersTotal, batchSize, orders } = this.props;
    const ordersLeftToFetch = ordersTotal - orders.length;

    return ordersLeftToFetch > batchSize ? batchSize : ordersLeftToFetch;
  };

  getIsEndOfHistory = () => {
    const { ordersTotal, orders } = this.props;

    return ordersTotal === orders.length;
  };

  closeModal = () => this.modal.hide();

  openModal = () => this.modal.show();

  renderVoucherIcon = order => {
    const { paidByVoucher, isInvoiceable } = order;

    if (paidByVoucher > 0 && isInvoiceable) {
      return <GiftIcon className="OrderTable__voucher_display_icon" />;
    }

    return null;
  };

  renderStatus = order => {
    const statusClass = {
      'OrderTable__status--ok': OK_STATUSES.indexOf(order.orderStatus) !== -1,
      'OrderTable__status--warn': WARN_STATUSES.indexOf(order.orderStatus) !== -1,
      'OrderTable__status--danger': DANGER_STATUSES.indexOf(order.orderStatus) !== -1,
    };
    const label = getStatusLabel(order.orderStatus);

    if (label) {
      return (
        <div className={classNames('OrderTable__status', statusClass)}>
          <span className="OrderTable__status_icon" />
          <span className="OrderTable__status_text">{label}</span>
        </div>
      );
    }

    return <span>-</span>;
  };

  renderTableBody = () => {
    const { orders } = this.props;
    const mapRows = order => {
      const isLoadingDetails = order.isFetchingDetails;
      // TODO: ask about billing date
      const billingTimeLabel = moment(order.billingDate).format(this.dateFormatStr);
      const rowClass = classNames('OrderTable__body_row', {
        'is-loading': isLoadingDetails,
      });
      const onRowClick = () => {
        if (!isLoadingDetails) {
          this.onRowClick(order);
        }
      };

      return (
        <TableRow key={order.id} className={rowClass}>
          <TableRowColumn>{billingTimeLabel}</TableRowColumn>
          <TableRowColumn>{order.description}</TableRowColumn>
          <TableRowColumn>{this.renderStatus(order)}</TableRowColumn>
          <TableRowColumn>{formatPrice(order.effectivePaidByVoucher, true)}</TableRowColumn>
          <TableRowColumn>{formatPrice(order.effectivePaidOnDelivery, true)}</TableRowColumn>
          <TableRowColumn>{formatPrice(order.totalValue, true)}</TableRowColumn>
          <TableRowColumn>{formatPrice(order.leadFee, true)}</TableRowColumn>
          <TableRowColumn>
            {this.renderVoucherIcon(order)}
            <span>{formatPrice(order.dealerProfit, true)}</span>
          </TableRowColumn>
          <TableRowColumn onClick={onRowClick} className="OrderTable__details_col">
            <Tooltip position="right" title={this.detailsTooltip}>
              <MagnifierIcon className="OrderTable__details_icon" onClick={onRowClick} />
            </Tooltip>
          </TableRowColumn>
        </TableRow>
      );
    };

    return orders.map(mapRows);
  };

  renderTableFooter = () => {
    const footerTableProps = {
      selectable: false,
    };
    const footerBodyProps = {
      ...this.tableBodyProps,
      showRowHover: false,
    };
    const {
      dealerProfitAmount,
      grossLeadFeeAmount,
      paidByVouchersAmount,
      paidOnDeliveryAmount,
      totalOrderedAmount,
    } = this.props;

    const { isLoading } = this.props;
    const showMoreClass = classNames('OrderTable__show_more', {
      'OrderTable__show_more--loading': isLoading,
    });
    const ordersLeft = this.getNumberOfOrdersLeft();

    return (
      <React.Fragment>
        {!this.getIsEndOfHistory() && (
          <div className={showMoreClass} onClick={this.fetchMoreOrders}>
            <FormattedMessage id="order_history.show_more" defaultMessage="SHOW MORE" />
            {ordersLeft && ordersLeft > 0 && <span>&nbsp;({this.getNumberOfOrdersLeft()})</span>}
          </div>
        )}
        <div className="OrderTable__footer_wrapper">
          <Table className="OrderTable__footer" {...footerTableProps}>
            <TableBody {...footerBodyProps}>
              <TableRow>
                <TableRowColumn>TOTAL</TableRowColumn>
                <TableRowColumn />
                <TableRowColumn />
                <TableRowColumn>{formatPrice(paidByVouchersAmount, true)}</TableRowColumn>
                <TableRowColumn>{formatPrice(paidOnDeliveryAmount, true)}</TableRowColumn>
                <TableRowColumn>{formatPrice(totalOrderedAmount, true)}</TableRowColumn>
                <TableRowColumn>{formatPrice(grossLeadFeeAmount, true)}</TableRowColumn>
                <TableRowColumn>{formatPrice(dealerProfitAmount, true)}</TableRowColumn>
                <TableRowColumn />
              </TableRow>
            </TableBody>
          </Table>
        </div>
      </React.Fragment>
    );
  };

  render() {
    const { hasScroll } = this.state;
    const componentClass = classNames('OrderTable', {
      'has-scroll': hasScroll,
    });

    return (
      <div className={componentClass}>
        <div className="OrderTable__content">
          <div className="OrderTable__orders_wrapper">
            <Table className="OrderTable__table" {...this.tableProps}>
              <TableHeader className="OrderTable__header" {...this.tableHeaderProps}>
                <TableRow>
                  <TableHeaderColumn>
                    <FormattedMessage id="date" defaultMessage="DATE" />
                  </TableHeaderColumn>
                  <TableHeaderColumn>
                    <FormattedMessage id="order_history.order_id" defaultMessage="ORDER ID" />
                  </TableHeaderColumn>
                  <TableHeaderColumn>STATUS</TableHeaderColumn>
                  <TableHeaderColumn>
                    <FormattedMessage id="coupon" defaultMessage="COUPON" />
                  </TableHeaderColumn>
                  <TableHeaderColumn>
                    <FormattedMessage
                      id="order_history.received_from_consumer"
                      defaultMessage="RECEIVED FROM<br />CONSUMER"
                    />
                  </TableHeaderColumn>
                  <TableHeaderColumn>
                    <FormattedMessage
                      id="order_history.order_total"
                      defaultMessage="ORDER<br />TOTAL"
                    />
                  </TableHeaderColumn>
                  <TableHeaderColumn>
                    <FormattedMessage
                      id="order_history.chama_comission"
                      defaultMessage="CHAMA<br />COMISSION"
                    />
                  </TableHeaderColumn>
                  <TableHeaderColumn>
                    <FormattedMessage
                      id="order_history.dealer_net_result"
                      defaultMessage="DEALER NET<br />RESULT"
                    />
                  </TableHeaderColumn>
                  <TableHeaderColumn />
                </TableRow>
              </TableHeader>
              <TableBody className="OrderTable__body" {...this.tableBodyProps}>
                {this.renderTableBody()}
              </TableBody>
            </Table>
          </div>
          {this.renderTableFooter()}
        </div>
        <Modal
          ref={ref => {
            this.modal = ref;
          }}
          confirmButtonText={<FormattedMessage id="closeLabel" defaultMessage="Close" />}
        >
          <div className="OrderTable__modal_content">
            {this.state.orderDetails && (
              <HistoryOrderDetails
                details={this.state.orderDetails}
                dealer={this.props.dealer}
                onCloseBtnClick={this.closeModal}
                analyticsTrack={this.props.analyticsTrack}
              />
            )}
          </div>
        </Modal>
      </div>
    );
  }
}

OrderTableLayout.propTypes = propTypes;

export default injectIntl(OrderTableLayout);



// WEBPACK FOOTER //
// ../ui-components/components/OrderTable/index.jsx